import { useState } from "react"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import CenteredModal from "@/src/common/centered-modal/CenteredModal"
import { Iconify } from "@/src/common/iconify"
import { createUser } from "@/src/services/internal"
import {
    WORKSPACE_USER_ROLE_OPTIONS,
    DEFAULT_WORKSPACE_ROLE,
    type WorkspaceRoleValue,
} from "@/src/features/internalUsers/constants"

// ----------------------------------------------------------------------

export interface AddNewUserModalProps {
    open: boolean
    onClose: () => void
    onSuccess?: () => void
    /** Workspace to add the user to (from admin context; not read from localStorage). */
    workspaceId: string
}

export function AddNewUserModal({ open, onClose, onSuccess, workspaceId }: AddNewUserModalProps) {
    const [selectedRole, setSelectedRole] = useState<WorkspaceRoleValue>(DEFAULT_WORKSPACE_ROLE)
    const [email, setEmail] = useState("")
    const [name, setName] = useState("")
    const [setNewPassword, setSetNewPassword] = useState("")
    const [confirmNewPassword, setConfirmNewPassword] = useState("")
    const [submitting, setSubmitting] = useState(false)

    const handleClose = () => {
        setSelectedRole(DEFAULT_WORKSPACE_ROLE)
        setEmail("")
        setName("")
        setSetNewPassword("")
        setConfirmNewPassword("")
        onClose()
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!workspaceId) return
        setSubmitting(true)
        try {
            const response = await createUser(workspaceId, {
                email,
                name: email,
                password: setNewPassword,
                role: selectedRole,
            })
            console.log("response", response)
            onSuccess?.()
            handleClose()
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <CenteredModal open={open} onClose={handleClose} width={440}>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    "& > *": { flexShrink: 0 },
                }}
            >
                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                bgcolor: "primary.lighter",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Iconify icon="mdi:user-plus" width={24} sx={{ color: "primary.main" }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600}>
                            Add New User
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={handleClose} aria-label="Close">
                        <Iconify icon="mingcute:close-line" width={24} />
                    </IconButton>
                </Stack>

                <Stack spacing={3.5}>
                    <TextField
                        fullWidth
                        select
                        label="Role"
                        value={selectedRole}
                        onChange={(event) => setSelectedRole(event.target.value as WorkspaceRoleValue)}
                        size="small"
                    >
                        {WORKSPACE_USER_ROLE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        label="Email ID"
                        placeholder="Enter New Email ID"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        size="small"
                    />

                    <TextField
                        fullWidth
                        label="Name"
                        placeholder="Enter Name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Iconify icon="mdi:user-outline" width={20} sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Set New Password"
                        placeholder="Set New Password"
                        type="password"
                        value={setNewPassword}
                        onChange={(e) => setSetNewPassword(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Iconify icon="mdi:key-outline" width={20} sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Confirm New Password"
                        placeholder="Confirm New Password"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Iconify icon="mdi:key-outline" width={20} sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={submitting}
                        startIcon={<Iconify icon="mdi:user-plus" width={22} />}
                        sx={{ mt: 1 }}
                    >
                        {submitting ? "Adding..." : "Add User"}
                    </Button>
                </Stack>
            </Box>
        </CenteredModal>
    )
}
